import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { fileService } from '../services/apiService';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { Image as ImageIcon, Loader2, ArrowLeft, CheckCircle } from 'lucide-react';
import { PhoneInput, normalizePhone } from '../components/ui/phone-input';

export default function ShareTargetPage() {
  const { currentUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [sharedImage, setSharedImage] = useState(null);   // blob URL for preview
  const [sharedFile, setSharedFile] = useState(null);     // File object for upload
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [salesmen, setSalesmen] = useState([]);
  const [catalogGroups, setCatalogGroups] = useState([]);
  const [catalogProducts, setCatalogProducts] = useState([]);

  const [form, setForm] = useState({
    client_name: '',
    client_phone: '',
    product_name: '',
    main_group: '',
    estimated_value: '',
    assigned_salesman_id: '',
  });

  // Load catalogs & salesmen
  useEffect(() => {
    if (!currentUser) return;
    Promise.all([
      api.get('/users/by-role/salesman').catch(() => ({ data: [] })),
      api.get('/catalog/main-groups').catch(() => ({ data: [] })),
      api.get('/catalog/products').catch(() => ({ data: [] })),
    ]).then(([s, mg, mp]) => {
      setSalesmen(s.data || []);
      setCatalogGroups(mg.data?.map(i => i.name) || []);
      setCatalogProducts(mp.data?.map(i => i.name) || []);
      // Auto-assign to current user if they are a salesman
      if (['salesman', 'stock_supervisor'].includes(currentUser.role)) {
        setForm(f => ({ ...f, assigned_salesman_id: currentUser.id }));
      }
    });
  }, [currentUser]);

  // Read the shared image from the service worker cache
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.get('incoming')) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const resp = await fetch('/shared-file');
        if (resp.ok) {
          const blob = await resp.blob();
          const fileName = resp.headers.get('X-File-Name') || 'shared-image.jpg';
          const file = new File([blob], fileName, { type: blob.type || 'image/jpeg' });
          setSharedFile(file);
          setSharedImage(URL.createObjectURL(blob));
        }
      } catch (e) {
        toast({ title: 'Could not load shared image', variant: 'destructive' });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Redirect to login if not authenticated, come back after
  useEffect(() => {
    if (!authLoading && !currentUser) {
      navigate('/login', { state: { returnTo: '/share-target?incoming=1' } });
    }
  }, [currentUser, authLoading]);

  const handleSubmit = async () => {
    if (!form.product_name) {
      toast({ title: 'Product name is required', variant: 'destructive' });
      return;
    }
    if (!form.assigned_salesman_id) {
      toast({ title: 'Please assign a salesman', variant: 'destructive' });
      return;
    }

    setSubmitting(true);
    try {
      // 1. Upload the shared image
      let imageUrl = null;
      if (sharedFile) {
        const uploadResult = await fileService.uploadFile(sharedFile);
        imageUrl = uploadResult.url;
      }

      // 2. Create the session
      const sessionRes = await api.post('/crm/sessions', {
        assigned_salesman_id: form.assigned_salesman_id,
        client_name: form.client_name || undefined,
        client_phone: form.client_phone || undefined,
        lead_source: 'WhatsApp',
        notes: 'Created via WhatsApp image share',
      });
      const sessionId = sessionRes.data._id;

      // 3. Add the lead
      await api.post(`/crm/sessions/${sessionId}/leads`, {
        product_name: form.product_name,
        main_group: form.main_group || undefined,
        estimated_value: parseFloat(form.estimated_value) || 0,
        description: imageUrl ? `Shared image: ${imageUrl}` : undefined,
      });

      // 4. Post image as first message in session so it appears in chat
      if (imageUrl) {
        await api.post(`/crm/sessions/${sessionId}/messages`, {
          content: imageUrl,
          type: 'image',
        });
      }

      // 5. Clear the cached shared file
      if ('caches' in window) {
        const cache = await caches.open('diaforge-share');
        await cache.delete('/shared-file');
      }

      toast({ title: 'Lead created!', description: `${form.product_name} lead created successfully` });
      navigate(`/lead-sessions/${sessionId}`);
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed to create lead', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#f0f2f5] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  const isSalesmanRole = ['salesman', 'stock_supervisor'].includes(currentUser?.role);

  return (
    <div className="min-h-screen bg-[#f0f2f5]">
      {/* Header */}
      <div className="bg-[#075E54] text-white px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-white">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="font-semibold text-base">New Lead from Image</h1>
          <p className="text-xs text-emerald-200">Shared from WhatsApp</p>
        </div>
      </div>

      <div className="max-w-lg mx-auto p-4 space-y-4">

        {/* Image Preview */}
        <div className="bg-white rounded-xl overflow-hidden shadow-sm">
          {sharedImage ? (
            <img src={sharedImage} alt="Shared" className="w-full max-h-64 object-contain bg-gray-50" />
          ) : (
            <div className="h-40 flex flex-col items-center justify-center text-gray-400 gap-2">
              <ImageIcon className="w-10 h-10" />
              <p className="text-sm">No image attached</p>
            </div>
          )}
        </div>

        {/* Form */}
        <div className="bg-white rounded-xl p-4 shadow-sm space-y-4">
          <h2 className="font-semibold text-gray-800 text-sm border-b pb-2">Lead Details</h2>

          {/* Product */}
          <div className="space-y-1">
            <Label className="text-xs text-gray-500">Product Name *</Label>
            <Select value={form.product_name} onValueChange={v => setForm(f => ({ ...f, product_name: v }))}>
              <SelectTrigger data-testid="share-product-select">
                <SelectValue placeholder="Select product" />
              </SelectTrigger>
              <SelectContent>
                {catalogProducts.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Main Group */}
          <div className="space-y-1">
            <Label className="text-xs text-gray-500">Main Group</Label>
            <Select value={form.main_group} onValueChange={v => setForm(f => ({ ...f, main_group: v }))}>
              <SelectTrigger>
                <SelectValue placeholder="Select group" />
              </SelectTrigger>
              <SelectContent>
                {catalogGroups.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Estimated Value */}
          <div className="space-y-1">
            <Label className="text-xs text-gray-500">Estimated Value (₹)</Label>
            <Input
              type="number"
              placeholder="e.g. 50000"
              value={form.estimated_value}
              onChange={e => setForm(f => ({ ...f, estimated_value: e.target.value }))}
              data-testid="share-estimated-value"
            />
          </div>

          <h2 className="font-semibold text-gray-800 text-sm border-b pb-2 pt-1">Client Details <span className="text-gray-400 font-normal">(optional)</span></h2>

          {/* Client Name */}
          <div className="space-y-1">
            <Label className="text-xs text-gray-500">Client Name</Label>
            <Input
              placeholder="e.g. Priya Sharma"
              value={form.client_name}
              onChange={e => setForm(f => ({ ...f, client_name: e.target.value }))}
              data-testid="share-client-name"
            />
          </div>

          {/* Client Phone */}
          <div className="space-y-1">
            <Label className="text-xs text-gray-500">Client Phone</Label>
            <PhoneInput
              value={normalizePhone(form.client_phone)}
              onChange={v => setForm(f => ({ ...f, client_phone: v }))}
            />
          </div>

          {/* Salesman — only shown for admin/owner */}
          {!isSalesmanRole && (
            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Assign Salesman *</Label>
              <Select value={form.assigned_salesman_id} onValueChange={v => setForm(f => ({ ...f, assigned_salesman_id: v }))}>
                <SelectTrigger data-testid="share-salesman-select">
                  <SelectValue placeholder="Select salesman" />
                </SelectTrigger>
                <SelectContent>
                  {salesmen.map(s => <SelectItem key={s.id} value={s.id}>{s.username}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Submit */}
        <Button
          className="w-full bg-[#075E54] hover:bg-emerald-800 text-white h-12 text-base font-semibold rounded-xl"
          onClick={handleSubmit}
          disabled={submitting}
          data-testid="share-submit-btn"
        >
          {submitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <CheckCircle className="w-5 h-5 mr-2" />}
          {submitting ? 'Creating Lead...' : 'Create Lead'}
        </Button>
      </div>
    </div>
  );
}
